import { Fragment } from "react";

function parseInline(text) {
  const parts = [];
  const re = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const token = m[0];
    if (token.startsWith("**")) {
      parts.push(
        <strong key={`${m.index}-b`} className="text-[var(--text-primary)] font-medium">
          {token.slice(2, -2)}
        </strong>
      );
    } else {
      const labelEnd = token.indexOf("](");
      const label = token.slice(1, labelEnd);
      const href = token.slice(labelEnd + 2, -1);
      const external = href.startsWith("http");
      parts.push(
        <a
          key={`${m.index}-a`}
          href={href}
          className="text-[var(--primary)] hover:underline"
          {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        >
          {label}
        </a>
      );
    }
    last = m.index + token.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : [text];
}

export default function LegalMarkdown({ source }) {
  const lines = String(source || "").replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let listItems = [];
  let paraLines = [];

  const flushPara = () => {
    const text = paraLines.join(" ").trim();
    paraLines = [];
    if (!text) return;
    blocks.push(
      <p key={`p-${blocks.length}`} className="text-sm text-[var(--text-secondary)] leading-relaxed mt-3">
        {parseInline(text)}
      </p>
    );
  };

  const flushList = () => {
    if (!listItems.length) return;
    blocks.push(
      <ul key={`ul-${blocks.length}`} className="list-disc pl-5 mt-3 space-y-2 text-sm text-[var(--text-secondary)]">
        {listItems.map((item, i) => (
          <li key={i} className="leading-relaxed">
            {parseInline(item)}
          </li>
        ))}
      </ul>
    );
    listItems = [];
  };

  const flushAll = () => {
    flushPara();
    flushList();
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      flushAll();
      continue;
    }

    if (trimmed === "---") {
      flushAll();
      blocks.push(<hr key={`hr-${blocks.length}`} className="border-[var(--border-default)] my-8" />);
      continue;
    }

    if (trimmed.startsWith("# ")) {
      flushAll();
      blocks.push(
        <h2
          key={`h2-${blocks.length}`}
          className="font-heading text-2xl md:text-3xl font-semibold tracking-tight text-[var(--text-primary)] mt-10 first:mt-0"
        >
          {trimmed.slice(2)}
        </h2>
      );
      continue;
    }

    if (trimmed.startsWith("## ")) {
      flushAll();
      blocks.push(
        <h3
          key={`h3-${blocks.length}`}
          className="font-heading text-lg font-semibold text-[var(--text-primary)] mt-8"
        >
          {trimmed.slice(3)}
        </h3>
      );
      continue;
    }

    if (trimmed.startsWith("* ")) {
      flushPara();
      listItems.push(trimmed.slice(2));
      continue;
    }

    flushList();
    paraLines.push(trimmed);
  }

  flushAll();

  return <div className="legal-markdown">{blocks.map((b, i) => <Fragment key={i}>{b}</Fragment>)}</div>;
}
